import * as Sentry from '@sentry/react';
import PQueue from 'p-queue';
import { Address } from 'viem';

import { api, cacheTags, IConnectedActionParams } from 'modules/api';
import { asAddress } from 'modules/api/utils/asAddress';
import { BASE_PQUEUE_CONFIG } from 'modules/common/const';
import { getIsOwnerPoolEndpoint } from 'modules/ownerPanel/actions/isPoolOwner';

interface IGetOwnerPoolAddressesArgs extends IConnectedActionParams {
  poolAddresses: Address[];
}

export const { useGetOwnerPoolAddressesQuery } = api.injectEndpoints({
  endpoints: build => ({
    getOwnerPoolAddresses: build.query<Address[], IGetOwnerPoolAddressesArgs>({
      queryFn: async ({ poolAddresses, connectionKey }, { dispatch }) => {
        const queue = new PQueue(BASE_PQUEUE_CONFIG);

        const requests = await Promise.all(
          poolAddresses.map(poolAddress =>
            queue.add(async () => {
              try {
                const isOwner = await dispatch(
                  getIsOwnerPoolEndpoint.initiate({
                    connectionKey,
                    poolAddress,
                  }),
                ).unwrap();

                return isOwner ? poolAddress : null;
              } catch (error) {
                Sentry.withScope(scope => {
                  scope.setExtra('poolAddress', poolAddress);
                  Sentry.captureException(error);
                });
                return null;
              }
            }),
          ),
        );
        const results = await Promise.allSettled(requests);

        const ownerPoolAddresses = results
          .map(result => (result.status === 'fulfilled' ? result.value : null))
          .filter((data): data is Address => !!(data && asAddress(data)));

        return { data: ownerPoolAddresses };
      },
      providesTags: [cacheTags.pools, cacheTags.account],
    }),
  }),
});
